import { usePathname } from "next/navigation";
import { useRouter } from "next/router";

import { RegisterStep } from "../components/Modals/RegisterModalsContainer";
import { SHOWCASE_CONTENT } from "../components/SolutionShowcase/helpers/showcaseContent";
import {
  SHOWCASE_STEP_BY_REGISTER_STEP,
  SOLUTION_SHOWCASE_EXPERIMENT_KEY,
} from "../components/SolutionShowcase/helpers/useSolutionShowcaseExperiment";
import { SignUpSessionStorage } from "../store/SignUpSessionStorage";

import { useReferredByOrganizationId } from "./useReferredByOrganizationId";

/**
 * Hook for navigating between register flow steps
 * Handles both variant A (v3) and variant B (v4 with category) flows
 * Also handles referral flow which always uses non-category paths
 */
export const useRegisterNavigator = () => {
  const router = useRouter();
  const pathname = usePathname();
  const isReferral = pathname?.includes("/referral") ?? false;
  const { referredByOrganizationId } = useReferredByOrganizationId();

  const variant = SignUpSessionStorage.getRegisterVariant();
  const category = SignUpSessionStorage.getRegisterCategory();

  /**
   * Builds URL for any step
   * - Referral flow: no category, preserves referredByOrganizationId
   * - Regular flow variant A: no category
   * - Regular flow variant B: includes category in path
   */
  const buildStepUrl = (step: RegisterStep, searchParams?: Record<string, string>): string => {
    const basePath = isReferral ? "/register/referral" : "/register";

    const shouldIncludeCategory =
      !isReferral &&
      variant === "B" &&
      category &&
      step !== "individual-type" &&
      step !== "individual-eligibility" &&
      step !== "kindbee";

    const isShowcaseSession =
      shouldIncludeCategory &&
      SignUpSessionStorage.getRegisterExperiment(SOLUTION_SHOWCASE_EXPERIMENT_KEY)?.variant === "test" &&
      Boolean(SHOWCASE_CONTENT[category]) &&
      Boolean(SHOWCASE_STEP_BY_REGISTER_STEP[step]);

    const path = shouldIncludeCategory
      ? `${basePath}${isShowcaseSession ? "/b" : ""}/${step}/${category}`
      : `${basePath}/${step}`;

    // Build search params, preserving referredByOrganizationId for referral flows
    const allParams: Record<string, string> = { ...searchParams };
    if (isReferral && referredByOrganizationId) {
      allParams.referredByOrganizationId = referredByOrganizationId;
    }

    if (Object.keys(allParams).length > 0) {
      const params = new URLSearchParams(allParams);
      return `${path}?${params.toString()}`;
    }
    return path;
  };

  // Navigation methods with router.push (async for awaiting)
  const goToStep = (step: RegisterStep, searchParams?: Record<string, string>) =>
    router.push(buildStepUrl(step, searchParams));
  const goToMethodChoice = async () => goToStep("method-choice");
  const goToUserInfo = async (email?: string) => goToStep("user-info", email ? { email } : undefined);
  const goToEligibility = async () => goToStep("eligibility");
  const goToOrganization = async (searchParams?: Record<string, string>) => goToStep("organization", searchParams);
  const goToQualification = async () => goToStep("qualification");
  const goToIndividualType = async () => goToStep("individual-type");
  const goToIndividualEligibility = async () => goToStep("individual-eligibility");
  const goToKindbee = async (fundsBeneficiary?: string) =>
    goToStep("kindbee", fundsBeneficiary ? { fundsBeneficiary } : undefined);

  return {
    variant,
    category,
    isReferral,
    buildStepUrl,
    goToMethodChoice,
    goToUserInfo,
    goToEligibility,
    goToOrganization,
    goToQualification,
    goToIndividualType,
    goToIndividualEligibility,
    goToKindbee,
  };
};
